/**
 * The panel's WebSocket endpoint, derived from the same two env vars the REST
 * client uses.
 *
 * `VITE_WS_API_URL` is documented as a full URL (`ws://localhost:8000`, see
 * README) but was consumed as a bare HOST, so the documented value produced
 * `ws://ws://localhost:8000/api/v1/ws` and both the command fallback and the
 * event stream connected to nothing — completion frames never arrived
 * (review finding). Accept either form: a URL with any of ws/wss/http/https
 * supplies both scheme and host; a bare host rides the API's scheme.
 */
export const resolveWsUrl = (input: {
  apiBase: string;
  wsApiUrl?: string | null;
  location: { host: string; protocol: string };
}): string => {
  const apiBase = input.apiBase.replace(/\/$/, '');
  const apiIsAbsolute = /^https?:\/\//.test(apiBase);
  const apiSecure =
    apiBase.startsWith('https') ||
    (!apiIsAbsolute && input.location.protocol === 'https:');

  const configured = (input.wsApiUrl ?? '').trim();
  if (configured) {
    const match = /^(wss?|https?):\/\/([^/]+)/i.exec(configured);
    if (match) {
      const scheme = match[1].toLowerCase();
      const secure = scheme === 'wss' || scheme === 'https';
      return `${secure ? 'wss:' : 'ws:'}//${match[2]}/api/v1/ws`;
    }
    // Bare host: honour it, keep the API's scheme.
    return `${apiSecure ? 'wss:' : 'ws:'}//${configured.replace(/\/$/, '')}/api/v1/ws`;
  }

  const host = apiIsAbsolute
    ? apiBase.replace(/^https?:\/\//, '')
    : input.location.host;
  return `${apiSecure ? 'wss:' : 'ws:'}//${host}/api/v1/ws`;
};
